<template>
    <div>
        <NavBar></NavBar>
        <div class="row">
            <MarketWatch class="col-md-4 d-none d-md-block"></MarketWatch>
            <div class="col-md-8 d-flex align-items-center justify-content-center vh-100" v-if="$store.state.holdings.length == 0">
                <div class="empty-state">
                    <div class="empty-icon">
                        <i class="bi bi-collection"></i>
                    </div>
                    <h5>No Holdings Yet</h5>
                    <p>Your investment portfolio will appear here</p>
                </div>
            </div>
            <div class="col-md-8" v-else>
                <div class="page-container">
                    <!-- Page Header -->
                    <div class="page-header">
                        <div class="header-left">
                            <h4 class="page-title">
                                <i class="bi bi-collection"></i>
                                Holdings
                            </h4>
                            <span class="item-count">{{ $store.state.holdings.length }} items</span>
                        </div>
                        <div class="total-pl-badge" :class="plPositive ? 'positive' : 'negative'">
                            <span class="pl-label">Total P&L</span>
                            <span class="pl-amount">{{ getTotalPl() }}</span>
                        </div>
                    </div>

                    <!-- Desktop Table -->
                    <div class="data-table d-none d-md-block">
                        <div class="table-header">
                            <div class="col-instrument">Instrument</div>
                            <div class="col-qty">Qty</div>
                            <div class="col-avg">Avg</div>
                            <div class="col-ltp">LTP</div>
                            <div class="col-pl">P&L</div>
                            <div class="col-chg">Chg</div>
                            <div class="col-action"></div>
                        </div>
                        <div class="table-row" v-for="(item, index) in $store.state.holdings" :key="item.instrumentToken" :class="{'faded': item.qty == 0}">
                            <div class="col-instrument">
                                <span class="instrument-name">{{ item.instrumentName }}</span>
                            </div>
                            <div class="col-qty">{{ item.qty }}</div>
                            <div class="col-avg">{{ item.priceAvg.toFixed(2) }}</div>
                            <div class="col-ltp">{{ $store.state.ticker[item.instrumentToken] }}</div>
                            <div class="col-pl" :class="item.plColor ? 'positive' : 'negative'">{{ getPlSign(item) }}</div>
                            <div class="col-chg">{{ getPercentageChange(item.priceAvg, $store.state.ticker[item.instrumentToken]) }}</div>
                            <div class="col-action">
                                <div class="action-btns" v-if="item.qty != 0">
                                    <button class="btn-action exit" @click="exit(index)" title="Exit">
                                        <i class="bi bi-dash-circle"></i>
                                    </button>
                                    <button class="btn-action add" @click="add(index)" title="Add">
                                        <i class="bi bi-plus-circle"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Mobile Cards -->
                    <div class="d-block d-md-none mobile-container">
                        <div class="holding-card" v-for="(item, index) in $store.state.holdings" :key="index" :class="{'faded': item.qty == 0}">
                            <div class="card-header-row">
                                <span class="instrument-name">{{ item.instrumentName }}</span>
                                <span class="change-badge" :class="item.plColor ? 'positive' : 'negative'">
                                    {{ getPercentageChange(item.priceAvg, $store.state.ticker[item.instrumentToken]) }}
                                </span>
                            </div>
                            <div class="card-details">
                                <div class="detail-item">
                                    <span class="detail-label">Qty</span>
                                    <span class="detail-value">{{ item.qty }}</span>
                                </div>
                                <div class="detail-item">
                                    <span class="detail-label">Avg</span>
                                    <span class="detail-value">{{ item.priceAvg.toFixed(2) }}</span>
                                </div>
                                <div class="detail-item">
                                    <span class="detail-label">LTP</span>
                                    <span class="detail-value">{{ $store.state.ticker[item.instrumentToken] }}</span>
                                </div>
                                <div class="detail-item">
                                    <span class="detail-label">P&L</span>
                                    <span class="detail-value" :class="item.plColor ? 'positive' : 'negative'">{{ getPlSign(item) }}</span>
                                </div>
                            </div>
                            <div class="card-actions" v-if="item.qty != 0">
                                <button class="btn-card exit" @click="exit(index)">
                                    <i class="bi bi-dash-circle"></i> Exit
                                </button>
                                <button class="btn-card add" @click="add(index)">
                                    <i class="bi bi-plus-circle"></i> Add
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import NavBar from '@/components/main/NavBar.vue'
import MarketWatch from '@/components/marketwatch/MarketWatch'
import { getHoldings } from '@/service/restService'
import '@/service/utils.js'
export default {
  name: 'Holding',
  components: {
    NavBar,
    MarketWatch
  },
  data () {
    return {
      plPositive: false
    }
  },
  methods: {
    getHoldings: function () {
      getHoldings().then((response) => {
        this.$store.commit('updateHolding', response.data)
      }).catch((error) => {
        this.$store.commit('addToast', {
          title: 'Failure!',
          type: 'error',
          message: error.response.data.message[0]
        })
      })
    },
    getPlSign: function (item) {
      var res = this.getPl(item)
      item.plColor = res >= 0
      return res > 0 ? '+' + res : '' + res
    },
    getPl: function (item) {
      if (item.type === 'LONG') {
        return (item.pl + (this.$store.state.ticker[item.instrumentToken] - item.priceAvg) * item.qty).toFixed(2)
      } else if (item.type === 'SHORT') {
        return (item.pl + (item.priceAvg - this.$store.state.ticker[item.instrumentToken]) * item.qty * -1).toFixed(2)
      } else {
        return (item.pl).toFixed(2)
      }
    },
    getTotalPl: function () {
      var totalPL = 0.0
      for (var i = 0; i < this.$store.state.holdings.length; i++) {
        var pos = this.$store.state.holdings[i]
        var pl = this.getPl(pos)
        totalPL = totalPL + parseFloat(pl)
      }
      this.plPositive = totalPL >= 0
      return totalPL > 0 ? '+' + totalPL.toFixed(2) : '' + totalPL.toFixed(2)
    },
    exit: function (id) {
      var pos = this.$store.state.holdings[id]
      var type = pos.qty > 0 ? 'SELL' : 'BUY'
      var mw = { }
      mw.instrumentToken = pos.instrumentToken
      mw.displayName = pos.instrumentName
      mw.qty = Math.abs(pos.qty)
      mw.duration = 'CNC'
      mw.lotSize = pos.lotSize
      this.$store.commit('setBuySell', {'type': type, 'mw': mw})
    },
    add: function (id) {
      var pos = this.$store.state.holdings[id]
      var type = pos.qty > 0 ? 'BUY' : 'SELL'
      var mw = { }
      mw.instrumentToken = pos.instrumentToken
      mw.displayName = pos.instrumentName
      mw.qty = Math.abs(pos.qty)
      mw.duration = 'CNC'
      mw.lotSize = pos.lotSize
      this.$store.commit('setBuySell', {'type': type, 'mw': mw})
    }
  },
  created () {
    if (this.$store.state.holdingFirstTimeFlag) {
      this.getHoldings()
      this.$store.commit('disableHoldingFirstTime')
    }
  }
}
</script>

<style scoped>
/* Page Container */
.page-container {
  padding: 20px;
}

/* Empty State */
.empty-state {
  text-align: center;
  padding: 40px;
}

.empty-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: var(--bs-tertiary-bg);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}

.empty-icon i {
  font-size: 36px;
  color: var(--bs-secondary-color);
}

.empty-state h5 {
  color: var(--bs-body-color);
  margin-bottom: 8px;
}

.empty-state p {
  color: var(--bs-secondary-color);
  margin: 0;
}

/* Page Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  flex-wrap: wrap;
  gap: 12px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-title {
  font-weight: 600;
  color: var(--bs-body-color);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 20px;
}

.page-title i {
  color: var(--bs-primary);
}

.item-count {
  background: var(--bs-tertiary-bg);
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 12px;
  color: var(--bs-secondary-color);
}

.total-pl-badge {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding: 10px 16px;
  border-radius: 10px;
  background: var(--bs-tertiary-bg);
}

.total-pl-badge.positive {
  background: rgba(11, 218, 81, 0.1);
}

.total-pl-badge.negative {
  background: rgba(239, 1, 7, 0.1);
}

.total-pl-badge .pl-label {
  font-size: 11px;
  color: var(--bs-secondary-color);
}

.total-pl-badge .pl-amount {
  font-size: 18px;
  font-weight: 700;
}

.total-pl-badge.positive .pl-amount {
  color: #0BDA51;
}

.total-pl-badge.negative .pl-amount {
  color: #EF0107;
}

/* Desktop Table */
.data-table {
  background: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 12px;
  overflow: hidden;
}

.table-header {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 80px;
  padding: 12px 16px;
  background: var(--bs-tertiary-bg);
  font-size: 12px;
  font-weight: 600;
  color: var(--bs-secondary-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.table-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr 1fr 80px;
  padding: 14px 16px;
  border-bottom: 1px solid var(--bs-border-color);
  align-items: center;
  transition: background-color 0.2s;
}

.table-row:last-child {
  border-bottom: none;
}

.table-row:hover {
  background: var(--bs-tertiary-bg);
}

.table-row.faded {
  opacity: 0.5;
}

.table-row .instrument-name {
  font-weight: 600;
  color: var(--bs-body-color);
}

.table-row .positive {
  color: #0BDA51;
  font-weight: 600;
}

.table-row .negative {
  color: #EF0107;
  font-weight: 600;
}

.action-btns {
  display: flex;
  gap: 6px;
  opacity: 0;
  transition: opacity 0.2s;
}

.table-row:hover .action-btns {
  opacity: 1;
}

.btn-action {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-action.exit {
  background: rgba(239, 1, 7, 0.1);
  color: #EF0107;
}

.btn-action.exit:hover {
  background: #EF0107;
  color: white;
}

.btn-action.add {
  background: rgba(13, 110, 253, 0.1);
  color: #0d6efd;
}

.btn-action.add:hover {
  background: #0d6efd;
  color: white;
}

/* Mobile Cards */
.mobile-container {
  padding: 0;
}

.holding-card {
  background: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.holding-card.faded {
  opacity: 0.5;
}

.card-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.card-header-row .instrument-name {
  font-weight: 600;
  font-size: 15px;
  color: var(--bs-body-color);
}

.change-badge {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 8px;
  border-radius: 6px;
}

.change-badge.positive {
  background: rgba(11, 218, 81, 0.1);
  color: #0BDA51;
}

.change-badge.negative {
  background: rgba(239, 1, 7, 0.1);
  color: #EF0107;
}

.card-details {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  padding: 12px 0;
  border-top: 1px solid var(--bs-border-color);
  border-bottom: 1px solid var(--bs-border-color);
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detail-label {
  font-size: 11px;
  color: var(--bs-secondary-color);
}

.detail-value {
  font-size: 14px;
  font-weight: 600;
  color: var(--bs-body-color);
}

.detail-value.positive {
  color: #0BDA51;
}

.detail-value.negative {
  color: #EF0107;
}

.card-actions {
  display: flex;
  gap: 10px;
  margin-top: 12px;
}

.btn-card {
  flex: 1;
  padding: 10px;
  border-radius: 8px;
  border: none;
  font-size: 13px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-card.exit {
  background: rgba(239, 1, 7, 0.1);
  color: #EF0107;
}

.btn-card.exit:hover {
  background: #EF0107;
  color: white;
}

.btn-card.add {
  background: rgba(13, 110, 253, 0.1);
  color: #0d6efd;
}

.btn-card.add:hover {
  background: #0d6efd;
  color: white;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .page-container {
    padding: 15px;
  }

  .page-header {
    flex-direction: column;
    align-items: stretch;
  }

  .header-left {
    justify-content: space-between;
  }

  .total-pl-badge {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/holding/Holding.vue